import keys from "config/keys.jsx";
import {
  emitSessionExpired,
  getAccessIssuedAt,
  hasAuthenticatedSession,
  refreshAuthSession,
} from "auth/sessionCoordinator";
import { normalizeSessionErrorMessage } from "auth/sessionMessages";

const DEFAULT_CREDENTIALS = "include";
const JSON_CONTENT_TYPE = "application/json";

const isAbsoluteUrl = (url) => /^https?:\/\//i.test(url);

const normalizeBaseUrl = (baseUrl = keys.URL) => String(baseUrl || "").replace(/\/+$/, "");

const normalizeEndpoint = (endpoint = "") => {
  const value = String(endpoint || "");
  if (!value) return "";
  return value.startsWith("/") || isAbsoluteUrl(value) ? value : `/${value}`;
};

const findTokenInStoredPayload = (payload) => (
  payload?.token
  || payload?.accessToken
  || payload?.accessConfirmationToken
  || payload?.jwtResponse?.jwtToken
  || payload?.data?.token
  || payload?.data?.accessToken
  || payload?.data?.accessConfirmationToken
  || payload?.data?.jwtResponse?.jwtToken
  || payload?.data?.userDetails?.token
  || payload?.data?.userDetails?.accessToken
  || payload?.data?.userDetails?.accessConfirmationToken
  || payload?.data?.userDetails?.jwtResponse?.jwtToken
  || payload?.payload?.token
  || payload?.payload?.accessToken
  || payload?.payload?.accessConfirmationToken
  || payload?.payload?.jwtResponse?.jwtToken
  || payload?.payload?.data?.token
  || payload?.payload?.data?.accessToken
  || payload?.payload?.data?.accessConfirmationToken
  || payload?.payload?.data?.jwtResponse?.jwtToken
  || ""
);

const readStoredJson = (key) => {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const getAuthToken = () => {
  try {
    return localStorage.getItem("token")
      || localStorage.getItem("authToken")
      || localStorage.getItem("accessToken")
      || findTokenInStoredPayload(readStoredJson("user"))
      || findTokenInStoredPayload(readStoredJson("userInfo"))
      || findTokenInStoredPayload(readStoredJson("userData"))
      || "";
  } catch {
    return "";
  }
};

export const createApiLogger = () => ({
  info: () => {},
  success: () => {},
  error: () => {}
});

const defaultLogger = createApiLogger();

export class ApiError extends Error {
  constructor(message, response, payload) {
    super(message);
    this.name = "ApiError";
    this.status = response?.status;
    this.statusText = response?.statusText;
    this.payload = payload;
    this.response = response;
  }
}

export const interpolateEndpoint = (endpoint, params = {}) => (
  String(endpoint || "").replace(/\{([^}]+)\}/g, (_, key) => encodeURIComponent(params[key] ?? ""))
);

export const appendPathSegments = (endpoint, ...segments) => {
  const base = String(endpoint || "").replace(/\/+$/, "");
  const suffix = segments
    .filter((segment) => segment !== undefined && segment !== null && segment !== "")
    .map((segment) => encodeURIComponent(segment))
    .join("/");

  return suffix ? `${base}/${suffix}` : base;
};

export const buildQueryString = (query) => {
  if (!query) return "";

  const searchParams = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (Array.isArray(value)) {
      value.forEach((item) => searchParams.append(key, item));
      return;
    }
    searchParams.append(key, value);
  });

  const value = searchParams.toString();
  return value ? `?${value}` : "";
};

export const buildRequestUrl = (baseUrl, endpoint, { pathParams, query, queryParams } = {}) => {
  const interpolated = interpolateEndpoint(endpoint, pathParams);
  const endpointWithSlash = normalizeEndpoint(interpolated);
  const queryString = buildQueryString(query || queryParams);
  const separator = endpointWithSlash.includes("?") ? "&" : "?";
  const resolvedQuery = queryString ? `${separator}${queryString.replace(/^\?/, "")}` : "";
  const url = `${endpointWithSlash}${resolvedQuery}`;

  return isAbsoluteUrl(url) ? url : `${normalizeBaseUrl(baseUrl)}${url}`;
};

const getStatusMessage = (response, payload) => {
  const responseMessage = normalizeSessionErrorMessage(payload?.message || payload?.error);

  switch (response.status) {
    case 400:
      return responseMessage || "Bad request. Please check your input.";
    case 401:
      return responseMessage || "Unauthorized. Please log in.";
    case 403:
      return responseMessage || "Forbidden. You do not have permission to access this resource.";
    case 404:
      return responseMessage || "Not found. The requested resource does not exist.";
    case 405:
      return responseMessage || "Method Not Allowed.";
    case 500:
      return responseMessage || "Internal server error. Please try again later.";
    case 503:
      return responseMessage || "Service unavailable. Please try again later.";
    default:
      return responseMessage || response.statusText || "API request failed.";
  }
};

const parseResponse = async (response, responseType) => {
  if (response.status === 204) return null;
  if (responseType === "blob") return response.blob();
  if (responseType === "text") return response.text();

  const contentType = response.headers.get("content-type") || "";
  const text = await response.text();
  if (!text) return null;

  if (contentType.includes(JSON_CONTENT_TYPE)) {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

const shouldSendBody = (method, payload) => (
  payload !== undefined
  && payload !== null
  && !["GET", "HEAD"].includes(String(method || "").toUpperCase())
);

// The access token expires quickly (short-lived by design) and the refresh
// endpoint renews it as an httpOnly cookie rather than returning it in the
// response body, so a client holding an older token in localStorage has no
// direct way to learn the cookie was refreshed. When a request comes back
// 401, silently refresh the cookie and retry once WITHOUT the (stale)
// Authorization header so the browser's freshly-renewed cookie is what
// authenticates the retry — avoids surfacing a spurious "not found"/"forbidden"
// error for what is really just an expired access token. A 403 is a real
// authorization failure and must not replay side-effecting requests.
const REFRESH_ENDPOINT = "/api/auth/refresh-token";

const isRefreshableEndpoint = (endpoint) => {
  const value = String(endpoint || "");
  return !value.includes(REFRESH_ENDPOINT)
    && !value.includes("/api/auth/login")
    && !value.includes("/api/auth/logout");
};

const shouldRefreshAndRetry = (status, endpoint, skipAuth) => {
  if (skipAuth || !isRefreshableEndpoint(endpoint)) return false;
  if (status === 401) return true;
  return false;
};

const runFetch = async (url, requestMethod, requestHeaders, credentials, signal, requestPayload, isFormData, rawBody) => (
  fetch(url, {
    method: requestMethod,
    headers: requestHeaders,
    credentials,
    signal,
    body: shouldSendBody(requestMethod, requestPayload)
      ? isFormData || rawBody
        ? requestPayload
        : JSON.stringify(requestPayload)
      : undefined
  })
);

export const executeApiRequest = async (baseUrl, endpoint, options = {}) => {
  const {
    body,
    credentials = DEFAULT_CREDENTIALS,
    headers,
    logger = defaultLogger,
    logPayload = false,
    method = "GET",
    okStatuses = [],
    pathParams,
    payload,
    query,
    queryParams,
    rawBody = false,
    responseType,
    returnResponse = false,
    signal,
    skipAuth = false
  } = options;

  const requestMethod = String(method || "GET").toUpperCase();
  const requestPayload = body !== undefined ? body : payload;
  const isFormData = typeof FormData !== "undefined" && requestPayload instanceof FormData;
  const url = buildRequestUrl(baseUrl, endpoint, { pathParams, query, queryParams });

  const buildHeaders = (includeAuthToken) => {
    const cookieBackedSession = hasAuthenticatedSession() || Boolean(getAccessIssuedAt());
    const authToken = includeAuthToken && !cookieBackedSession ? getAuthToken() : "";
    return {
      ...(shouldSendBody(requestMethod, requestPayload) && !isFormData && !rawBody ? { "Content-Type": JSON_CONTENT_TYPE } : {}),
      ...(!skipAuth && authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      ...(headers || {})
    };
  };

  logger.info("request", {
    method: requestMethod,
    url,
    ...(logPayload ? { payload: requestPayload } : {})
  });

  const attempt = async (includeAuthToken) => {
    const response = await runFetch(
      url, requestMethod, buildHeaders(includeAuthToken), credentials, signal, requestPayload, isFormData, rawBody
    );
    const responsePayload = await parseResponse(response, responseType);
    return { response, responsePayload };
  };

  try {
    let { response, responsePayload } = await attempt(true);

    if (shouldRefreshAndRetry(response.status, endpoint, skipAuth)) {
      const refreshed = await refreshAuthSession(baseUrl, { force: true });
      if (refreshed) {
        ({ response, responsePayload } = await attempt(false));
      }
    }

    if (returnResponse) {
      return { response, payload: responsePayload };
    }

    if (!response.ok && !okStatuses.includes(response.status)) {
      if (response.status === 401) {
        try {
          localStorage.setItem("tokenExpired", "true");
        } catch {
          // ignore storage failures (private mode, quota, etc.)
        }

        if (hasAuthenticatedSession()) {
          emitSessionExpired("unauthorized");
        }
      }
      throw new ApiError(getStatusMessage(response, responsePayload), response, responsePayload);
    }

    logger.success("success", { method: requestMethod, url, status: response.status });
    return responsePayload;
  } catch (error) {
    logger.error("failed", {
      method: requestMethod,
      url,
      message: error?.message,
      status: error?.status
    });
    throw error;
  }
};

export default executeApiRequest;
